import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Custom-domain host resolution.
 *
 * When the app is loaded on a studio's own domain (e.g. idlichutnee.com), we
 * resolve that host to its studio slug via the backend and render the
 * storefront at the root path instead of /s/<slug>. On the platform host
 * (preview / emergent.host / localhost) we behave exactly as before.
 *
 * A `?__host=` query override is supported so the flow can be previewed and
 * tested from the shared platform URL.
 */
const HostTenantContext = createContext({
  ready: false,
  mode: "platform", // platform | custom | custom-unresolved
  slug: null,
  tenant: null,
});

const PLATFORM_SUFFIXES = [
  "preview.emergentagent.com",
  "emergentagent.com",
  "emergent.host",
  "localhost",
  "127.0.0.1",
];

// The product's own primary domain(s) — these render the normal platform site
// (marketing, login, admin, studio, /s/:slug), NOT a studio storefront.
// Configurable via REACT_APP_PLATFORM_HOSTS (comma-separated); defaults cover vawworld.tv.
const PLATFORM_HOSTS = (process.env.REACT_APP_PLATFORM_HOSTS || "vawworld.tv,www.vawworld.tv")
  .split(",")
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean);

export function currentHost() {
  try {
    const params = new URLSearchParams(window.location.search);
    const override = params.get("__host");
    if (override) return override.toLowerCase().trim();
    return (window.location.hostname || "").toLowerCase().trim();
  } catch (_e) {
    return "";
  }
}

export function isPlatformHost(h) {
  if (!h) return true;
  const host = h.replace(/^www\./, "");
  if (PLATFORM_HOSTS.some((p) => h === p || host === p || host === p.replace(/^www\./, ""))) return true;
  return PLATFORM_SUFFIXES.some((s) => h === s || h.endsWith("." + s) || h.endsWith(s));
}

export function HostTenantProvider({ children }) {
  const [state, setState] = useState({ ready: false, mode: "platform", slug: null, tenant: null });

  useEffect(() => {
    const h = currentHost();
    if (isPlatformHost(h)) {
      setState({ ready: true, mode: "platform", slug: null, tenant: null });
      return;
    }
    let cancelled = false;
    api
      .get("/public/resolve-domain", { params: { host: h } })
      .then((r) => {
        if (!cancelled) {
          setState({ ready: true, mode: "custom", slug: r.data.slug, tenant: r.data.tenant });
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Unknown/unverified host -> fall back to the normal platform site
          // (never a dead error screen).
          setState({ ready: true, mode: "platform", slug: null, tenant: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <HostTenantContext.Provider value={state}>{children}</HostTenantContext.Provider>;
}

export const useHostTenant = () => useContext(HostTenantContext);
